import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import {
  inviteRecipientEmailSchema,
  normalizeInvitationEmail,
  VISIBLE_INVITE_ROLES,
  type OrganizationMemberRoleName,
  type TeamInviteRole,
} from "@multi-chat/shared";
import { isApiError } from "../../api/client.js";
import { AppNotice } from "../../components/common/AppNotice.js";
import {
  invitationSubmitFormError,
  teamInviteLimitFeedback,
  teamNoActiveOwnerMessage,
} from "./team-errors.js";
import { createTeamInvitations } from "../../api/team-api.js";
import { useNotifications } from "../notifications/notification-context.js";
import { Button } from "../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";

interface StagedInvite {
  key: number;
  displayName: string;
  email: string;
  role: TeamInviteRole;
}

let stageKey = 1;

function nextKey(): number {
  stageKey += 1;
  return stageKey;
}

const FIELD_HELPER_CLASS = "mt-1 block min-h-[1.1rem] text-[0.7rem] leading-[1.1rem] text-destructive";

/** Shared by the staged-invite header and every data row. */
const STAGED_INVITE_GRID =
  "grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.15fr)_5.5rem_2.25rem] items-center gap-x-3 px-3";
const STAGED_INVITE_CELL = "flex min-w-0 items-center justify-center text-center";

export function InviteMembersDialog({
  open,
  organizationId,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  organizationId: number;
  actorRole: OrganizationMemberRoleName;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { pushNotification } = useNotifications();
  const fixedRole: TeamInviteRole = VISIBLE_INVITE_ROLES[0];
  const [draftName, setDraftName] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [staged, setStaged] = useState<StagedInvite[]>([]);
  const [draftEmailError, setDraftEmailError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [seatNotice, setSeatNotice] = useState<{ remaining: number; excess: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraftName("");
    setDraftEmail("");
    setStaged([]);
    setDraftEmailError(null);
    setRowErrors({});
    setFormError(null);
    setSeatNotice(null);
    setSubmitting(false);
  }, [open]);

  const trimmedName = draftName.trim();
  const trimmedEmail = draftEmail.trim();
  const emailValid = inviteRecipientEmailSchema.safeParse(trimmedEmail).success;
  const normalizedDraft = trimmedEmail.length > 0 ? normalizeInvitationEmail(trimmedEmail) : "";
  const duplicateInStaged =
    normalizedDraft.length > 0 &&
    staged.some((item) => normalizeInvitationEmail(item.email) === normalizedDraft);
  const canAdd =
    !submitting &&
    trimmedName.length > 0 &&
    emailValid &&
    !duplicateInStaged;

  const stagedEmails = useMemo(
    () => new Set(staged.map((item) => normalizeInvitationEmail(item.email))),
    [staged],
  );

  function reset(): void {
    setDraftName("");
    setDraftEmail("");
    setStaged([]);
    setDraftEmailError(null);
    setRowErrors({});
    setFormError(null);
    setSeatNotice(null);
  }

  function close(next: boolean): void {
    if (submitting) return;
    if (!next) reset();
    onOpenChange(next);
  }

  function addDraft(): void {
    const name = draftName.trim();
    const email = draftEmail.trim();
    if (name.length === 0) return;
    const parsed = inviteRecipientEmailSchema.safeParse(email);
    if (!parsed.success) {
      setDraftEmailError(t("team.invite.errors.invalid_email"));
      return;
    }
    const normalized = normalizeInvitationEmail(parsed.data);
    if (stagedEmails.has(normalized)) {
      setDraftEmailError(t("team.invite.errors.duplicate_email"));
      return;
    }
    setStaged((current) => [
      ...current,
      {
        key: nextKey(),
        displayName: name,
        email: normalized,
        role: fixedRole,
      },
    ]);
    setDraftName("");
    setDraftEmail("");
    setDraftEmailError(null);
    setFormError(null);
    setSeatNotice(null);
  }

  async function submit(): Promise<void> {
    if (staged.length === 0 || submitting) return;
    setSubmitting(true);
    setFormError(null);
    setSeatNotice(null);
    setRowErrors({});
    try {
      await createTeamInvitations(
        organizationId,
        staged.map((row) => ({
          displayName: row.displayName,
          email: row.email,
          role: fixedRole,
        })),
      );
      reset();
      onOpenChange(false);
      onCreated();
    } catch (error) {
      const limitFeedback = teamInviteLimitFeedback(t, error, staged.length);
      if (limitFeedback?.kind === "partial") {
        setSeatNotice({ remaining: limitFeedback.remaining, excess: limitFeedback.excess });
        return;
      }
      if (limitFeedback?.kind === "none") {
        pushNotification({
          kind: "error",
          message: t("team.invite.noSeats"),
          detail: t("team.invite.noSeatsDetail"),
        });
        reset();
        onOpenChange(false);
        return;
      }
      if (limitFeedback?.kind === "generic") {
        setFormError(limitFeedback.message);
        return;
      }
      const noOwnerMessage = teamNoActiveOwnerMessage(t, error);
      if (noOwnerMessage) {
        setFormError(noOwnerMessage);
        return;
      }
      let rowCodes: string[] = [];
      if (isApiError(error) && error.details) {
        const next: Record<number, string> = {};
        staged.forEach((row, index) => {
          const code = error.details?.[`row${index}`];
          if (code) next[row.key] = code;
        });
        rowCodes = Object.values(next);
        setRowErrors(next);
      }
      const formMessage = invitationSubmitFormError(t, rowCodes);
      if (formMessage) setFormError(formMessage);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={close}>
      <AlertDialogContent className="w-[min(calc(100vw-2rem),42rem)]">
        <AlertDialogCloseButton
          label={t("common.close")}
          disabled={submitting}
          onClose={() => close(false)}
        />
        <AlertDialogTitle>{t("team.invite.title")}</AlertDialogTitle>
        <AlertDialogDescription>{t("team.invite.subtitle")}</AlertDialogDescription>

        <section className="grid gap-2" data-testid="invite-staged-section" aria-label={t("team.invite.stagedHeading")}>
          <p className="m-0 text-[0.7rem] font-semibold tracking-[0.04em] text-muted uppercase">
            {t("team.invite.stagedHeading")}
          </p>
          {staged.length > 0 ? (
            <div className="overflow-hidden rounded-md border border-line bg-surface-muted/40" data-testid="invite-staged-list">
              <div className={`${STAGED_INVITE_GRID} border-b border-line py-1.5 text-[0.65rem] font-medium text-muted`}>
                <span className={STAGED_INVITE_CELL}>{t("team.columns.name")}</span>
                <span className={STAGED_INVITE_CELL}>{t("team.columns.email")}</span>
                <span className={STAGED_INVITE_CELL}>{t("team.columns.role")}</span>
                <span className={`${STAGED_INVITE_CELL} sr-only`}>{t("team.invite.removeRow")}</span>
              </div>
              <ul className="m-0 list-none p-0">
                {staged.map((row) => (
                  <li
                    key={row.key}
                    className={`${STAGED_INVITE_GRID} min-h-11 border-b border-line/70 py-2 last:border-b-0`}
                    data-testid={`invite-staged-${row.key}`}
                  >
                    <span className={STAGED_INVITE_CELL} title={row.displayName}>
                      <span className="max-w-full truncate text-[0.8125rem] font-medium text-foreground">
                        {row.displayName}
                      </span>
                    </span>
                    <span className={STAGED_INVITE_CELL} title={row.email}>
                      <span className="max-w-full truncate text-[0.8125rem] text-muted" dir="ltr">
                        {row.email}
                      </span>
                    </span>
                    <span className={STAGED_INVITE_CELL}>
                      <span className="inline-flex items-center rounded-md border border-accent/30 bg-accent-soft px-2 py-0.5 text-[0.7rem] font-semibold text-accent">
                        {t("organization.role.admin")}
                      </span>
                    </span>
                    <span className={STAGED_INVITE_CELL}>
                    <button
                      type="button"
                      className="inline-flex size-7 items-center justify-center rounded-md border-0 bg-transparent text-muted transition-colors hover:bg-error-muted hover:text-error focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]"
                      aria-label={t("team.invite.removeRow")}
                      data-testid={`invite-remove-${row.key}`}
                      disabled={submitting}
                      onClick={() => {
                        setSeatNotice(null);
                        setStaged((current) => current.filter((item) => item.key !== row.key));
                      }}
                    >
                      <Trash2 size={14} aria-hidden="true" />
                    </button>
                    </span>
                    {rowErrors[row.key] ? (
                      <span className="col-span-full text-[0.7rem] text-destructive" role="alert">
                        {t(`team.invite.errors.${rowErrors[row.key]}`, {
                          defaultValue: t("team.invite.sendError"),
                        })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="m-0 rounded-md border border-dashed border-line px-3 py-2.5 text-[0.75rem] text-muted">
              {t("team.invite.stagedEmpty")}
            </p>
          )}
        </section>

        <section className="grid gap-2 border-t border-line pt-3" data-testid="invite-draft-section">
          <p className="m-0 text-[0.7rem] font-semibold tracking-[0.04em] text-muted uppercase">
            {t("team.invite.draftHeading")}
          </p>
          <div
            className="grid items-start gap-2 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.15fr)_5.5rem_auto]"
            data-testid="invite-draft-row"
          >
            <label className="grid min-w-0">
              <span className="mb-1 text-[0.7rem] font-medium text-muted">{t("team.columns.name")}</span>
              <input
                value={draftName}
                className="h-9 rounded-md border border-line-strong bg-surface px-3 text-[0.8125rem] focus:border-accent focus:outline-none"
                placeholder={t("team.invite.namePlaceholder")}
                data-testid="invite-name"
                disabled={submitting}
                onChange={(event) => setDraftName(event.target.value)}
              />
              <span className={FIELD_HELPER_CLASS} aria-hidden="true" />
            </label>
            <label className="grid min-w-0">
              <span className="mb-1 text-[0.7rem] font-medium text-muted">{t("team.columns.email")}</span>
              <input
                value={draftEmail}
                dir="ltr"
                inputMode="email"
                autoComplete="off"
                className="h-9 rounded-md border border-line-strong bg-surface px-3 text-[0.8125rem] focus:border-accent focus:outline-none"
                placeholder={t("team.invite.emailPlaceholder")}
                data-testid="invite-email"
                disabled={submitting}
                aria-invalid={Boolean(draftEmailError)}
                aria-describedby="invite-email-helper"
                onChange={(event) => {
                  setDraftEmail(event.target.value);
                  setDraftEmailError(null);
                }}
                onBlur={() => {
                  if (trimmedEmail.length === 0) {
                    setDraftEmailError(null);
                    return;
                  }
                  if (!inviteRecipientEmailSchema.safeParse(trimmedEmail).success) {
                    setDraftEmailError(t("team.invite.errors.invalid_email"));
                  } else if (duplicateInStaged) {
                    setDraftEmailError(t("team.invite.errors.duplicate_email"));
                  }
                }}
              />
              <span
                id="invite-email-helper"
                className={FIELD_HELPER_CLASS}
                role={draftEmailError ? "alert" : undefined}
                data-testid="invite-email-error"
              >
                {draftEmailError ?? "\u00a0"}
              </span>
            </label>
            <div className="grid min-w-0">
              <span className="mb-1 text-[0.7rem] font-medium text-muted">{t("team.columns.role")}</span>
              <span
                className="inline-flex h-9 items-center rounded-md border border-line bg-surface-muted px-3 text-[0.8125rem] font-medium text-foreground"
                data-testid="invite-role"
              >
                {t("organization.role.admin")}
              </span>
              <span className={FIELD_HELPER_CLASS} aria-hidden="true" />
            </div>
            <div className="grid min-w-0">
              <span className="mb-1 text-[0.7rem] font-medium text-transparent select-none" aria-hidden="true">
                .
              </span>
              <Button
                type="button"
                size="sm"
                className="h-9"
                disabled={!canAdd}
                data-testid="invite-add"
                onClick={addDraft}
              >
                {t("team.invite.add")}
              </Button>
              <span className={FIELD_HELPER_CLASS} aria-hidden="true" />
            </div>
          </div>
        </section>

        {seatNotice ? (
          <AppNotice
            severity="warning"
            description={`${t("team.invite.seatsRemaining", { count: seatNotice.remaining })} ${t("team.invite.removeStaged", { count: seatNotice.excess })}`}
            testId="invite-seat-notice"
          />
        ) : null}

        {formError ? (
          <p className="m-0 text-[0.75rem] text-destructive" role="alert" data-testid="invite-form-error">
            {formError}
          </p>
        ) : null}

        <AlertDialogActions>
          <Button variant="outline" size="sm" onClick={() => close(false)} disabled={submitting}>
            {t("common.cancel")}
          </Button>
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={submitting || staged.length === 0}
            data-testid="invite-submit"
          >
            {submitting ? t("team.invite.sending") : t("team.invite.send")}
          </Button>
        </AlertDialogActions>
      </AlertDialogContent>
    </AlertDialog>
  );
}
